import { Badge } from "../ui/badge";
import { Eye, Users, MousePointerClick, TrendingUp } from "lucide-react";

const Kpi = ({ icon: Icon, iconClass, value, label }) => (
  <div className="bg-white rounded-xl border border-black/5 p-5">
    <Icon className={`w-5 h-5 mb-2 ${iconClass}`} strokeWidth={1.5} />
    <p className="font-heading font-black tracking-tighter text-3xl text-slate-900">{value}</p>
    <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mt-1">{label}</p>
  </div>
);

const ctrClass = (ctr) =>
  ctr >= 5 ? "text-emerald-600 font-bold" : ctr >= 1 ? "text-amber-600" : "text-slate-400";
const matchClass = (score) =>
  score >= 70 ? "text-emerald-600" : score >= 40 ? "text-amber-600" : "text-red-500";

const JobRow = ({ job }) => (
  <tr className="border-b border-black/5 last:border-0 hover:bg-slate-50/60 transition-colors">
    <td className="px-5 py-3">
      <p className="font-medium text-slate-900 truncate max-w-[200px]">{job.title}</p>
      <p className="text-xs text-slate-400">{job.status === "active" ? "Ativa" : "Fechada"}</p>
    </td>
    <td className="text-right px-4 py-3 text-slate-700">{job.views.toLocaleString("pt-PT")}</td>
    <td className="text-right px-4 py-3 text-slate-700">{job.applications_count}</td>
    <td className={`text-right px-4 py-3 ${ctrClass(job.ctr)}`}>{job.ctr}%</td>
    <td className={`text-right px-4 py-3 ${job.avg_match_score != null ? matchClass(job.avg_match_score) : "text-slate-300"}`}>
      {job.avg_match_score != null ? `${job.avg_match_score}%` : "—"}
    </td>
    <td className="text-right px-4 py-3 text-slate-600">{job.recent_apps_30d}</td>
    <td className="text-center px-4 py-3">
      {job.boosted ? (
        <Badge className={
          (job.boost_tier === "gold" || job.boost_tier === "destaque_max") ? "bg-amber-100 text-amber-800 text-[10px] hover:bg-amber-100 font-bold"
          : (job.boost_tier === "silver" || job.boost_tier === "destaque") ? "bg-sky-100 text-sky-800 text-[10px] hover:bg-sky-100 font-bold"
          : "bg-orange-100 text-orange-800 text-[10px] hover:bg-orange-100 font-bold"}>
          {(job.boost_tier === "gold" || job.boost_tier === "destaque_max") ? "GOLD"
           : job.boost_tier === "express" ? "EXPRESS"
           : "SILVER"}
        </Badge>
      ) : (
        <span className="text-slate-300 text-xs">—</span>
      )}
    </td>
  </tr>
);

/** KPIs e desempenho por vaga da empresa. */
export const AnalyticsPanel = ({ analytics }) => {
  if (!analytics) return <div className="text-center py-12 text-slate-400">A carregar analytics...</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Eye} iconClass="text-slate-400" label="Visualizações"
          value={analytics.total_views.toLocaleString("pt-PT")} />
        <Kpi icon={Users} iconClass="text-slate-400" label="Candidaturas" value={analytics.total_apps} />
        <Kpi icon={MousePointerClick} iconClass="text-amber-500" label="CTR global" value={`${analytics.overall_ctr}%`} />
        <Kpi icon={TrendingUp} iconClass="text-emerald-500" label="Match IA médio"
          value={analytics.avg_match_score != null ? `${analytics.avg_match_score}%` : "—"} />
      </div>

      {analytics.job_performance.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm">Nenhuma vaga ativa com dados disponíveis.</div>
      ) : (
        <div className="bg-white rounded-xl border border-black/5 overflow-hidden">
          <div className="px-5 py-4 border-b border-black/5">
            <p className="font-heading font-bold tracking-tighter text-slate-900">Performance por vaga</p>
            <p className="text-xs text-slate-500 mt-0.5">CTR = candidaturas ÷ visualizações × 100</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-black/5 text-xs uppercase tracking-[0.15em] font-bold text-slate-500">
                  <th className="text-left px-5 py-3">Vaga</th>
                  <th className="text-right px-4 py-3">Visitas</th>
                  <th className="text-right px-4 py-3">Candidaturas</th>
                  <th className="text-right px-4 py-3">CTR</th>
                  <th className="text-right px-4 py-3">Match IA</th>
                  <th className="text-right px-4 py-3">30d</th>
                  <th className="text-center px-4 py-3">Boost</th>
                </tr>
              </thead>
              <tbody>
                {analytics.job_performance.map((j) => <JobRow key={j.id} job={j} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className="text-xs text-slate-400 text-center">
        CTR de referência no setor: 2-8% para ofertas em destaque, 0.5-2% para ofertas orgânicas.
      </p>
    </div>
  );
};
